import { Box, Button, Center, Divider, Spinner, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';

import ButtonedRadioGroup from '../../../components/ButtonedRadioGroup';
import { useLEAccordionItemContext } from '../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import TextWithTooltip from '../../../components/TextWithTooltip';
import TimeAtAddress from '../../../components/TimeAtAddress';
import { AddressInput, Label } from '../../../components/formComponents';
import NumberInput from '../../../components/formComponents/NumberInput';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import { OWNERSHIP_TYPE, PARTIAL_STATES } from '../../../constants/addresses';
import { COUNTRY_CONFIG, getTempInfoCountry } from '../../../constants/countries';
import { LDFlags } from '../../../constants/flags';
import { statesArray } from '../../../constants/states';
import { UNABLE_TO_ASSIST_ERROR_MSG } from '../../../constants/tempInfos';
import {
  useGetMethodFiSessionStateQuery,
  useLikelyAddressLazyQuery,
  useTemporaryInfoUpdateMutation,
} from '../../../gql/generated/graphql';
import { useFlag, useTempInfo } from '../../../hooks';
import { identify } from '../../../services/analytics/rudderstack';
import { queryWithTimeout } from '../../../utils/queries';
import { lienholderNotAvailableInState } from '../../GettingStarted/components/WhoIsYourLease/utils';
import { ResidenceFormValues, residenceInitialValues, residenceValidationSchema } from '../utils';
import ProvinceDeadEnd from './ProvinceDeadEnd';
import SuggestedAddressCard from './SuggestedAddressCard';

type ResidenceProps = {
  skipPreviousAddress?: boolean;
  skipPrimaryResidenceDetails?: boolean;
  showWhyTooltip?: boolean;
  onComplete?: () => void;
  submitLabel?: string;
};

const Residence = ({
  skipPreviousAddress = false,
  skipPrimaryResidenceDetails = false,
  showWhyTooltip = true,
  onComplete,
  submitLabel,
}: ResidenceProps) => {
  const { updateState, index, setOpenAccordionIndex, isExpanded } = useLEAccordionItemContext();
  const { info, infoLoading } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const tempInfoData = info?.data;
  const country = getTempInfoCountry(tempInfoData);
  const addressAutofillEnabled =
    useFlag(LDFlags.COM_PRIMARY_ADDRESS_AUTOFILL) && COUNTRY_CONFIG[country].addressAutofill;
  const methodFiEnabled = !!tempInfoData?.is_methodfi_flow;

  const { data: methodFiData, loading: methodFiLoading } = useGetMethodFiSessionStateQuery({
    fetchPolicy: 'network-only',
    skip: !methodFiEnabled || !isExpanded,
  });

  const methodFiTempInfoData = methodFiData?.getMethodFiSessionState?.tempInfoFormattedData;

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();

  const [error, setError] = useState<string | null>(null);
  const [addressMode, setAddressMode] = useState<'suggested' | 'manual'>('suggested');
  const [isAddressLookupLoading, setIsAddressLookupLoading] = useState(false);
  const [submittedProvince, setSubmittedProvince] = useState<string>();

  const { regions, supportedRegions } = COUNTRY_CONFIG[country];
  const isUnsupportedRegion = (region: string) =>
    !!supportedRegions && !supportedRegions.includes(region);

  const province = submittedProvince ?? tempInfoData?.state;
  const unsupportedProvince = regions.find(
    (region) => region.value === province && isUnsupportedRegion(region.value),
  );

  const [getLikelyAddress, { data: likelyAddressData }] = useLikelyAddressLazyQuery();

  const fetchLikelyAddressWithTimeout = useCallback(
    async (phoneNumber?: string | null) => {
      setIsAddressLookupLoading(true);

      try {
        return await queryWithTimeout(() => getLikelyAddress({ variables: { phoneNumber } }));
      } finally {
        setIsAddressLookupLoading(false);
      }
    },
    [getLikelyAddress],
  );

  useEffect(() => {
    if (addressAutofillEnabled && tempInfoData?.phone_number && !tempInfoData?.address_line) {
      fetchLikelyAddressWithTimeout(tempInfoData?.phone_number);
    }
  }, [
    tempInfoData?.phone_number,
    tempInfoData?.address_line,
    addressAutofillEnabled,
    fetchLikelyAddressWithTimeout,
  ]);

  const suggestedAddress =
    addressAutofillEnabled && !tempInfoData?.address_line ? likelyAddressData?.likelyAddress : null;

  const onSubmitForm = async (values: ResidenceFormValues) => {
    updateState({ status: 'loading' });

    // Use suggested address if in suggested mode, otherwise use form values
    const addressData =
      addressMode === 'suggested' && suggestedAddress
        ? {
            address_line: suggestedAddress.address_line,
            address_line_2: suggestedAddress.address_line_2,
            city: suggestedAddress.city,
            state: suggestedAddress.state,
            county: suggestedAddress.county || values.county?.trim(),
            zip: suggestedAddress.zip,
            likely_address_accepted: true,
          }
        : {
            address_line: values.address_line,
            address_line_2: values.address_line_2,
            city: values.city.trim(),
            state: values.state,
            county: values.county.trim(),
            zip: values.zip.toString().toUpperCase(),
            likely_address_accepted: false,
          };

    const residenceData = {
      moved_states: values.moved_states === 'true',
      ...(skipPrimaryResidenceDetails
        ? {}
        : {
            primary_residence_type: values.primary_residence_type,
            primary_residence_years: values.primary_residence_years,
            primary_residence_months: values.primary_residence_months ?? 0,
            primary_residence_monthly_payment:
              values.primary_residence_type !== OWNERSHIP_TYPE.OWN_HOME_OUTRIGHT
                ? values.primary_residence_monthly_payment ?? undefined
                : undefined,
          }),
      secondary_residence_years: skipPreviousAddress ? undefined : values.secondary_residence_years,
      secondary_residence_months: skipPreviousAddress
        ? undefined
        : values.secondary_residence_months ?? 0,
      previous_address_line: skipPreviousAddress ? undefined : values.previous_address_line,
      previous_address_line_2: skipPreviousAddress ? undefined : values.previous_address_line_2,
      previous_city: skipPreviousAddress ? undefined : values.previous_city?.trim(),
      previous_state: skipPreviousAddress ? undefined : values.previous_state,
      previous_zip: skipPreviousAddress
        ? undefined
        : values.previous_zip
        ? values.previous_zip.toString().toUpperCase()
        : undefined,
      previous_county: skipPreviousAddress ? undefined : values.previous_county?.trim(),
    };

    const data = {
      ...addressData,
      ...residenceData,
    };

    await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data,
        },
      },
    });

    if (isUnsupportedRegion(addressData.state)) {
      updateState({ status: 'none' });
      setSubmittedProvince(addressData.state);
      return;
    }

    const isVin = !tempInfoData?.license_plate_number;
    if (lienholderNotAvailableInState(isVin ? values.state : null, tempInfoData?.lienholder_slug)) {
      updateState({ status: 'none' });
      setError(UNABLE_TO_ASSIST_ERROR_MSG);
      return;
    }

    identify({ address: addressData });

    updateState({ status: 'complete' });

    if (onComplete) {
      onComplete();
    } else {
      setOpenAccordionIndex(index + 1);
    }
  };

  if (!tempInfoData) {
    return null;
  }

  return (
    <Box>
      {showWhyTooltip && (
        <Center mb={{ base: 4, md: 3 }}>
          <TextWithTooltip label="Verifying your residential information gives lenders insight into your regular expenses and, if applicable, your payment history.">
            Why do we need this?
          </TextWithTooltip>
        </Center>
      )}
      <Formik
        enableReinitialize
        initialValues={residenceInitialValues(
          tempInfoData,
          methodFiEnabled ? methodFiTempInfoData : null,
        )}
        validationSchema={residenceValidationSchema({
          usingSuggestedAddress: addressMode === 'suggested' && !!suggestedAddress,
          suggestedAddressHasCounty: !!suggestedAddress?.county,
          skipPreviousAddress,
          skipPrimaryResidenceDetails,
          country,
        })}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmitForm}
      >
        {({ isSubmitting, errors, values, setFieldValue }) => {
          const hasErrors = Object.values(errors).some((value) => value !== '');
          const residenceType = values?.primary_residence_type;

          if (unsupportedProvince) {
            return (
              <ProvinceDeadEnd
                country={country}
                provinceName={unsupportedProvince.name}
                onChangeProvince={() => {
                  setFieldValue('state', '');
                  setSubmittedProvince('');
                }}
              />
            );
          }

          return (
            <Form>
              {isAddressLookupLoading || infoLoading || (methodFiEnabled && methodFiLoading) ? (
                <Center my={8}>
                  <Spinner size="xl" color="leaseEndBlue" />
                </Center>
              ) : (
                <>
                  {suggestedAddress && addressMode === 'suggested' && (
                    <SuggestedAddressCard
                      suggestedAddress={suggestedAddress}
                      onManualEntry={() => setAddressMode('manual')}
                    />
                  )}

                  {(!suggestedAddress || addressMode === 'manual') && (
                    <>
                      <Label>ADDRESS</Label>
                      <AddressInput dropLabel country={country} />
                      {suggestedAddress && (
                        <Center mt={3}>
                          <Button variant="link" onClick={() => setAddressMode('suggested')}>
                            Use suggested address
                          </Button>
                        </Center>
                      )}
                      {!skipPrimaryResidenceDetails && <Divider mt={8} mb={8} />}
                    </>
                  )}
                  {PARTIAL_STATES.includes(
                    suggestedAddress && addressMode === 'suggested'
                      ? suggestedAddress.state
                      : values.state,
                  ) && (
                    <>
                      <Label mt={5}>
                        DID YOU START YOUR LEASE IN{' '}
                        {statesArray
                          .find(
                            (state) =>
                              state.value ===
                              (suggestedAddress && addressMode === 'suggested'
                                ? suggestedAddress.state
                                : values.state),
                          )
                          ?.name.toUpperCase()}
                        ?
                      </Label>
                      <>
                        <ButtonedRadioGroup
                          value={values.moved_states}
                          columns={2}
                          onChange={(value) => setFieldValue('moved_states', value)}
                          options={[
                            { label: 'YES', value: 'false' },
                            { label: 'NO', value: 'true' },
                          ]}
                          name="moved_states"
                          defaultValue="false"
                        />
                        {errors.moved_states && (
                          <Box color="leaseEndRed">{errors.moved_states}</Box>
                        )}
                      </>
                      {!skipPrimaryResidenceDetails && <Divider mt={8} mb={8} />}
                    </>
                  )}
                </>
              )}

              {!skipPrimaryResidenceDetails && (
                <>
                  <Label mt={5}>LIVING SITUATION</Label>
                  <ButtonedRadioGroup
                    columns={{ base: 2, md: 4 }}
                    collapseOnMobile={false}
                    onChange={(value) => setFieldValue('primary_residence_type', value)}
                    options={[
                      { label: 'PAYING MORTGAGE', value: OWNERSHIP_TYPE.CURRENTLY_HAVE_A_MORTGAGE },
                      { label: 'RENTING', value: OWNERSHIP_TYPE['RENTING/_LEASING'] },
                      { label: 'OWN OUTRIGHT', value: OWNERSHIP_TYPE.OWN_HOME_OUTRIGHT },
                      { label: 'LIVE W/FAMILY', value: OWNERSHIP_TYPE.LIVING_WITH_FAMILY },
                    ]}
                    value={values.primary_residence_type}
                    name="primary_residence_type"
                    defaultValue={OWNERSHIP_TYPE.CURRENTLY_HAVE_A_MORTGAGE}
                  />
                  <Box color="leaseEndRed">{errors.primary_residence_type}</Box>
                  <Box
                    mt={8}
                    hidden={!residenceType || residenceType === OWNERSHIP_TYPE.OWN_HOME_OUTRIGHT}
                  >
                    <Label>MONTHLY PAYMENT</Label>
                    <NumberInput
                      name="primary_residence_monthly_payment"
                      type="decimal"
                      isMoney
                      maxW="200px"
                      maxLength={8}
                      _input={{ inputMode: 'numeric' }}
                      _container={{ alignItems: 'flex-start' }}
                    />
                  </Box>
                  <Divider mt={8} mb={8} />
                  <TimeAtAddress
                    values={values}
                    skipPreviousAddress={skipPreviousAddress}
                    country={country}
                  />
                </>
              )}
              <Center>
                <PrimaryButton
                  mt="30px"
                  mb={{
                    base: '20px',
                    lg: '0',
                  }}
                  loading={isSubmitting}
                  isDisabled={isAddressLookupLoading || infoLoading}
                  type="submit"
                  value={submitLabel}
                />
              </Center>
              {(error || hasErrors) && (
                <Text color="leaseEndRed" textAlign="center" mt={{ base: 0, lg: 4 }}>
                  {error || 'Review fields above for errors.'}
                </Text>
              )}
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default Residence;
